import { useState, useEffect, useRef } from "react";
import { Drop } from "../types";
import { AFFIRMATIONS, FONTS } from "../data/affirmations";
import { RouteMap } from "./RouteMap";

interface WalkScreenProps {
  drop: Drop;
  onEnd: (walkTime: number, steps: number, distanceMiles: number) => void;
}

export function WalkScreen({ drop, onEnd }: WalkScreenProps) {
  const [walkTime, setWalkTime] = useState(0);
  const [distanceMiles, setDistanceMiles] = useState(0);
  const [affirmIdx, setAffirmIdx] = useState(0);
  const [showMap, setShowMap] = useState(true);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const affirmRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const watchIdRef = useRef<number | null>(null);
  const walkMapRef = useRef<any>(null);
  const gpsTrailRef = useRef<any>(null);
  const gpsPointsRef = useRef<{ lat: number; lng: number }[]>([]);
  const lastPositionRef = useRef<{ lat: number; lng: number } | null>(null);

  const toRad = (deg: number) => deg * Math.PI / 180;

  const haversineDistance = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const R = 3958.8;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const x = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  };

  const centreOnUser = () => {
    if (userLocation && walkMapRef.current) {
      walkMapRef.current.panTo(userLocation);
      walkMapRef.current.setZoom(16);
    } else {
      alert("Could not get your location. Make sure location is enabled 📍");
    }
  };

  useEffect(() => {
    // Timer
    intervalRef.current = setInterval(() => {
      setWalkTime(t => t + 1);
    }, 1000);

    // Affirmations
    affirmRef.current = setInterval(() => {
      setAffirmIdx(i => (i + 1) % AFFIRMATIONS.length);
    }, 8000);

    // GPS tracking
    if (navigator.geolocation) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const newPoint = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setUserLocation(newPoint);

          // Pan map to user
          if (walkMapRef.current) {
            walkMapRef.current.panTo(newPoint);
          }

          // Calculate distance
          if (lastPositionRef.current) {
            const d = haversineDistance(lastPositionRef.current, newPoint);
            // Filter out GPS noise — only count if moved more than 5 metres
            if (d > 0.003) {
              setDistanceMiles(prev => parseFloat((prev + d).toFixed(2)));
              gpsPointsRef.current.push(newPoint);

              // Draw GPS trail on map
              const google = (window as any).google;
              if (google && walkMapRef.current) {
                if (gpsTrailRef.current) {
                  gpsTrailRef.current.setMap(null);
                }
                gpsTrailRef.current = new google.maps.Polyline({
                  path: gpsPointsRef.current,
                  geodesic: true,
                  strokeColor: "#4285F4",
                  strokeOpacity: 0.9,
                  strokeWeight: 4,
                });
                gpsTrailRef.current.setMap(walkMapRef.current);
              }
            }
          } else {
            gpsPointsRef.current.push(newPoint);
          }

          lastPositionRef.current = newPoint;
        },
        (err) => console.log("GPS error:", err),
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
    }

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(affirmRef.current);
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (gpsTrailRef.current) {
        gpsTrailRef.current.setMap(null);
      }
    };
  }, []);

  const fmt = (s: number) =>
    `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const steps = Math.round(distanceMiles * 2000);
  const pace = walkTime > 0 && distanceMiles > 0
    ? Math.round(walkTime / 60 / distanceMiles)
    : 0;

  return (
    <div style={{ minHeight: "100vh", background: "#FAF7F4" }}>
      <style>{FONTS + `
        @keyframes pulse { 0%,100% { transform:scale(1) } 50% { transform:scale(1.03) } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }
      `}</style>

      {/* Header */}
      <div style={{ background: `linear-gradient(160deg,${drop.cardBg},#FAF7F4)`, borderBottom: `1px solid ${drop.cardBorder}`, padding: "24px 20px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: "10px", letterSpacing: "4px", color: drop.accentColor, textTransform: "uppercase", marginBottom: "2px", fontFamily: "'DM Sans',sans-serif", fontWeight: 600 }}>now walking</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", fontWeight: 900, fontSize: "24px", color: "#1a1a1a", lineHeight: 1 }}>{drop.title}</div>
          <div style={{ fontSize: "12px", color: "#999", fontFamily: "'DM Sans',sans-serif", marginTop: "2px" }}>{drop.neighbourhood}</div>
        </div>
        <div style={{ fontSize: "32px" }}>{drop.emoji}</div>
      </div>

      <div style={{ padding: "16px 16px 40px", maxWidth: "480px", margin: "0 auto", animation: "fadeUp 0.4s ease" }}>

        {/* Map */}
        <div style={{ marginBottom: "14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <div style={{ fontSize: "11px", color: "#bbb", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif" }}>your route</div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                onClick={centreOnUser}
                style={{ fontSize: "12px", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, background: drop.accentColor, border: "none", cursor: "pointer", padding: "5px 12px", borderRadius: "50px" }}>
                Find me
              </button>
              <button
                onClick={() => setShowMap(m => !m)}
                style={{ fontSize: "12px", color: drop.accentColor, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
                {showMap ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          {showMap && (
            <RouteMap
              drop={drop}
              compact={false}
              userLocation={userLocation}
              mapRef={walkMapRef}
            />
          )}
        </div>

        {/* Spotify */}
        <button
          onClick={() => window.open(drop.spotifyUrl, "_blank")}
          style={{ display: "flex", alignItems: "center", gap: "14px", padding: "16px 20px", borderRadius: "20px", background: "#1DB954", color: "#000", border: "none", cursor: "pointer", marginBottom: "14px", boxShadow: "0 6px 20px rgba(29,185,84,0.3)", width: "100%" }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="black">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
          </svg>
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", opacity: 0.7, fontFamily: "'DM Sans',sans-serif" }}>tap to play</div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", fontWeight: 700, fontSize: "18px" }}>{drop.playlistEmoji} {drop.playlistName}</div>
            <div style={{ fontSize: "11px", opacity: 0.7, fontFamily: "'DM Sans',sans-serif" }}>{drop.bpm}</div>
          </div>
          <div style={{ fontSize: "24px", fontWeight: 900 }}>▶</div>
        </button>

        {/* Stats */}
        <div style={{ background: "#fff", borderRadius: "20px", padding: "20px 24px", marginBottom: "14px", border: "1px solid #EDE8E3", boxShadow: "0 2px 16px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "8px", textAlign: "center" }}>
            {[
              { val: fmt(walkTime), label: "Time", color: "#1a1a1a" },
              { val: `${distanceMiles.toFixed(2)}`, label: "Miles", color: "#0A7C5E" },
              { val: steps.toLocaleString(), label: "Steps", color: "#C9184A" },
              { val: pace > 0 ? `${pace}` : "--", label: "Min/mi", color: "#5B45D4" },
            ].map(s => (
              <div key={s.label}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontSize: "22px", color: s.color, lineHeight: 1 }}>{s.val}</div>
                <div style={{ fontSize: "9px", color: "#bbb", letterSpacing: "1.5px", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif", marginTop: "3px" }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* GPS indicator */}
          <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #F0ECE8", display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: userLocation ? "#0A7C5E" : "#bbb", boxShadow: userLocation ? "0 0 6px rgba(10,124,94,0.5)" : "none" }} />
            <div style={{ fontSize: "11px", color: "#bbb", fontFamily: "'DM Sans',sans-serif" }}>
              {userLocation ? "GPS tracking active" : "Waiting for GPS signal..."}
            </div>
          </div>
        </div>

        {/* Affirmation */}
        <div style={{ background: "linear-gradient(135deg,#FFF0F3,#FFF8F0)", border: "1px solid #F5DDD9", borderRadius: "16px", padding: "16px", marginBottom: "14px", textAlign: "center", animation: "pulse 4s ease infinite" }}>
          <p style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", fontSize: "15px", color: "#C9184A", lineHeight: 1.6, margin: 0 }}>{AFFIRMATIONS[affirmIdx]}</p>
        </div>

        <button
          onClick={() => onEnd(walkTime, steps, distanceMiles)}
          style={{ width: "100%", padding: "16px", borderRadius: "16px", border: "1.5px solid #1a1a1a", cursor: "pointer", background: "transparent", color: "#1a1a1a", fontSize: "14px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif" }}>
          End Walk
        </button>
      </div>
    </div>
  );
}
